"use client";

import { ThemeProvider as NextThemeProvider } from "next-themes";
import { memo, useEffect } from "react";

import {
  appConfig,
  defaultThemeConfig,
  themeColors,
  themeStorageKeys,
} from "~/config/app";

interface ThemeProviderProps {
  readonly children: React.ReactNode;
}

export const ThemeProvider = memo<ThemeProviderProps>(({ children }) => {
  useEffect(() => {
    const color = (() => {
      try {
        const rawConfig = window.localStorage.getItem(themeStorageKeys.color);

        if (!rawConfig) {
          return defaultThemeConfig.color;
        }

        const parsed = JSON.parse(rawConfig) as
          | {
              state?: { config?: { color?: string } };
              config?: { color?: string };
              color?: string;
            }
          | null;

        const storedColor =
          parsed?.state?.config?.color ?? parsed?.config?.color ?? parsed?.color;

        return themeColors.includes(storedColor as (typeof themeColors)[number])
          ? storedColor
          : defaultThemeConfig.color;
      } catch {
        return defaultThemeConfig.color;
      }
    })();

    document.documentElement.dataset.theme = color;
    document.body.dataset.theme = color;
  }, []);

  return (
    <NextThemeProvider
      attribute="class"
      defaultTheme={appConfig.theme.mode}
      storageKey={themeStorageKeys.mode}
      enableSystem
      disableTransitionOnChange
    >
      {children}
    </NextThemeProvider>
  );
});

ThemeProvider.displayName = "ThemeProvider";
